import axios from 'axios'

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://api.heet7studio.com'

if (!API_BASE) {
  console.error('[api] VITE_API_BASE_URL is not defined.')
}

// ── Axios instance (authenticated requests) ───────────────────────────────
const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('adminToken')
  if (token) cfg.headers.Authorization = `Bearer ${token}`
  return cfg
})

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('adminToken')
      localStorage.removeItem('adminUser')
      window.location.href = '/login'
    }
    return Promise.reject(err)
  }
)

// ── Auth ───────────────────────────────────────────────────────────────────
export const authAPI = {
  login:  (username, password) => api.post('/api/admin/login',  { username, password }),
  logout: ()                   => api.post('/api/admin/logout'),
}

// ── Dashboard ─────────────────────────────────────────────────────────────
export const dashboardAPI = {
  getStats: () => api.get('/api/admin/stats'),
}

// ── Services & Categories ─────────────────────────────────────────────────
export const servicesAPI = {
  getAll:  ()          => api.get('/api/admin/services'),
  create:  (data)      => api.post('/api/admin/services', data),
  update:  (id, data)  => api.put(`/api/admin/services/${id}`, data),
  delete:  (id)        => api.delete(`/api/admin/services/${id}`),
}

export const categoriesAPI = {
  getAll:  ()          => api.get('/api/admin/categories'),
  create:  (data)      => api.post('/api/admin/categories', data),
  update:  (id, data)  => api.put(`/api/admin/categories/${id}`, data),
  delete:  (id)        => api.delete(`/api/admin/categories/${id}`),
}

// ── Blog ──────────────────────────────────────────────────────────────────
export const blogAPI = {
  getAll:      ()              => api.get('/api/admin/blog'),
  getOne:      (id)            => api.get(`/api/admin/blog/${id}`),
  create:      (data)          => api.post('/api/admin/blog', data),
  update:      (id, data)      => api.put(`/api/admin/blog/${id}`, data),
  toggle:      (id)            => api.post(`/api/admin/blog/${id}/toggle`),
  delete:      (id)            => api.delete(`/api/admin/blog/${id}`),
  updateReel:  (id, url)       => api.put(`/api/admin/blog/${id}/reel`, { instagram_url: url }),
}

// ── Gallery ───────────────────────────────────────────────────────────────
export const galleryAPI = {
  getAll:  ()          => api.get('/api/admin/gallery'),
  create:  (data)      => api.post('/api/admin/gallery', data),
  update:  (id, data)  => api.put(`/api/admin/gallery/${id}`, data),
  delete:  (id)        => api.delete(`/api/admin/gallery/${id}`),
}

// ── Testimonials ──────────────────────────────────────────────────────────
export const testimonialsAPI = {
  getAll:  ()          => api.get('/api/admin/testimonials'),
  create:  (data)      => api.post('/api/admin/testimonials', data),
  update:  (id, data)  => api.put(`/api/admin/testimonials/${id}`, data),
  delete:  (id)        => api.delete(`/api/admin/testimonials/${id}`),
}

// ── Appointments ──────────────────────────────────────────────────────────
export const appointmentsAPI = {
  getAll:        (status = 'all') => api.get(`/api/admin/appointments?status=${status}`),
  updateStatus:  (id, status)     => api.put(`/api/admin/appointments/${id}/status`, { status }),
  delete:        (id)             => api.delete(`/api/admin/appointments/${id}`),
}

// ── Contacts ──────────────────────────────────────────────────────────────
export const contactsAPI = {
  getAll:     ()    => api.get('/api/admin/contacts'),
  toggleRead: (id)  => api.post(`/api/admin/contacts/${id}/read`),
  delete:     (id)  => api.delete(`/api/admin/contacts/${id}`),
}

// ── Site settings ─────────────────────────────────────────────────────────
export const siteAPI = {
  getSettings:    ()      => api.get('/api/admin/site'),
  updateSettings: (data)  => api.put('/api/admin/site', data),
}

// ── Nav ───────────────────────────────────────────────────────────────────
export const navAPI = {
  getAll:  ()          => api.get('/api/admin/nav'),
  create:  (data)      => api.post('/api/admin/nav', data),
  update:  (id, data)  => api.put(`/api/admin/nav/${id}`, data),
  delete:  (id)        => api.delete(`/api/admin/nav/${id}`),
}

// ── Account ───────────────────────────────────────────────────────────────
export const accountAPI = {
  update: (data) => api.put('/api/admin/account', data),
}

// ── File uploads (go to backend directly) ─────────────────────────────────
export const uploadFile = (file) => {
  const form = new FormData()
  form.append('image', file)
  return api.post('/upload/image', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export const uploadMultipleFiles = (files) => {
  const form = new FormData()
  files.forEach((f) => form.append('images', f))
  return api.post('/upload/images', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export const uploadPdf = (files) => {
  const form = new FormData()
  files.forEach((f) => form.append('pdfs', f))
  return api.post('/upload/pdf', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export const uploadVideo = (file) => {
  const form = new FormData()
  form.append('video', file)
  return api.post('/upload/video', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

// ── Image URL helper ──────────────────────────────────────────────────────
/**
 * getImageUrl(filename)
 * DB stores only filenames (e.g. '1234.jpg'). Constructs a full URL.
 * Handles legacy paths with /uploads/ prefix gracefully.
 */
export function getImageUrl(filename) {
  if (!filename) return ''
  if (/^https?:\/\//.test(filename)) return filename
  if (/^data:/.test(filename)) return filename
  // Strip any legacy path prefixes
  const clean = filename.replace(/^\/?(uploads\/)?/, '')
  if (!clean) return ''
  return `${API_BASE}/uploads/${clean}`
}

export default api
